import { observer } from "mobx-react-lite";
import Layout from "../../components/layout/Layout";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useStore } from "../../stores/root-store-context";
import { useEffect, useState } from "react";
import { Form, Input, Checkbox, Button, Select, Steps, InputNumber, App, Spin } from 'antd';
import { Test, TestLevelSetting, TestPointSetting, TestQuestion } from "../../stores/TestsStore";
import { AddTestFormQustions } from "../../components/test/AddTestFormQustions";
import { GoogleGenerativeAI } from "@google/generative-ai";
import { toJS } from "mobx";
import { Skill } from "../../models/Skill";
import { TypeTestQuestion } from "../../enums/TypeTestQuestion";

const timePerQuestionTypeInSeconds = {
    singleChoice: 30,
    multipleChoice: 45,
    writeCode: 90,
    fixCode: 90,
    openAnswer: 90
};

export interface QuestionForm {
    id: number,
    question: string;
    language: string;
    numberOfQuestion: number;
    options?: string[];
    correctAnswer?: string;
    numberPoints?: number;
    type: "singleChoice" | "multipleChoice" | "writeCode" | "fixCode" | "openAnswer";
    prompt?: string;
    code?: string;
}

interface TestForm {
    name: string,
    description: string | null,
    numberOfTests: number,
    selectedSkill: string,
    questionsToProvideCount: number,
    selectedLanguage: string,
    isOneLanguage: boolean,
    level: string,
    numberOfAttempts: number,
    maxTime: number,
}

export interface LevelSettings {
    maxTimeMin: number;
    attemptsMin: number;
    numberQuestions: number;
}

const defaultSettings: TestLevelSetting = {
    id: 0,
    level: '',
    minNumberOfGenerationQuestions: 0,
    minNumberAttempts: 0,
    minNumberOfQuestons: 0,
    minTime: 0,
    singleChoicePercentage: 0,
    multipleChoicePercentage: 0,
    writeCodePercentage: 0,
    fixCodePercentage: 0,
    openAnswerPercentage: 0,
};

const { Step } = Steps;

const EditOrAddTest = observer(() => {
    const GEMINI_MODEL = `${process.env.REACT_APP_AI_GEMINI_MODEL}`;
    const API_AI_KEY = `${process.env.REACT_APP_AI_GEMINI_API_KEY}`;
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { applicationStore, apiStore } = useStore();
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<TestForm>({
        name: '',
        description: '',
        numberOfTests: 0,
        selectedSkill: '',
        questionsToProvideCount: 0,
        selectedLanguage: 'en',
        isOneLanguage: applicationStore?.siteConfig?.isShowLanguage ? false : true,
        level: '',
        numberOfAttempts: 0,
        maxTime: 0,
    });
    const [questions, setQuestions] = useState<QuestionForm[]>([]);
    const [allCompanyTests, setAllCompanyTests] = useState<Test[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const { message } = App.useApp();
    const [formTest] = Form.useForm();
    const { testsStore } = useStore();
    const [levelSettings, setLevelSettings] = useState<LevelSettings>({
        maxTimeMin: 1,
        attemptsMin: 1,
        numberQuestions: 10
    });
    const [testSettings, setTestSettings] = useState<{
        junior: TestLevelSetting;
        middle: TestLevelSetting;
        senior: TestLevelSetting;
    }>();
    const [testPointSetting, setTestPointSetting] = useState<TestPointSetting[]>([]);
    const { id, isEdit } = useParams();
    const [existTest, setExistTest] = useState<Test | null>(null);
    const [isEditProcess, setIsEditProcess] = useState<boolean>(false);
    const [isOverride, setIsOverride] = useState<boolean>(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            if (!testsStore.isInitialized) {
                await testsStore.initializeStore();
            }

            const isEditBoolean = isEdit === 'true';
            setIsEditProcess(isEditBoolean);

            const allSettings = toJS(await testsStore.levelSettings);
            const allTestPointSetting = toJS(await testsStore.pointSettings);
            const allCompanyTests = toJS(await testsStore.testsCompany);

            setTestSettings({
                junior: allSettings.find((setting) => setting.level === 'junior') || defaultSettings,
                middle: allSettings.find((setting) => setting.level === 'middle') || defaultSettings,
                senior: allSettings.find((setting) => setting.level === 'senior') || defaultSettings,
            });
            setTestPointSetting(allTestPointSetting);
            setAllCompanyTests(allCompanyTests);

            await setDataEditProcess();

            setLoading(false);
        }

        fetchData();
    }, []);

    const setDataEditProcess = async () => {
        if (isEdit && id && isEdit === 'true') {

            let existTest = await testsStore.getTestById(id);

            if (existTest && existTest != null) {
                const newFormData: TestForm = {
                    description: existTest.description,
                    level: existTest.level ?? "junior",
                    questionsToProvideCount: existTest.questionsToProvideCount,
                    maxTime: existTest.maxTimeMinutes,
                    name: existTest.name ?? "",
                    numberOfAttempts: existTest.numberOfAttempts,
                    numberOfTests: existTest.numberOfQuestions,
                    selectedSkill: applicationStore.skills.find(s => s.skillId == existTest?.skillId?.toString())?.title || '',
                    isOneLanguage: existTest.language != "all",
                    selectedLanguage: "en",
                };
                var questionsForm = transformQuestions(existTest.testQuestions);

                setQuestions(questionsForm);
                setFormData(newFormData);
                formTest.setFieldsValue(newFormData);
                setExistTest(existTest);
            }
        }
    };

    const transformQuestions = (testQuestions: TestQuestion[]): QuestionForm[] => {
        return testQuestions.map(q => {
            const type = mapType(q.typeTestQuestionId);

            return {
                id: q.id,
                language: q.language,
                numberOfQuestion: q.numberOfQuestion,
                question: q.name,
                options: q.testOptions?.map(opt => opt.name) || [],
                correctAnswer: type === "multipleChoice"
                    ? q.correctAnswer?.split(",").map(ans => ans.trim()).join(", ") || ''
                    : q.correctAnswer || undefined,
                numberPoints: q.numberPoints,
                type: type,
                prompt: q.prompt || undefined,
                code: q.code || undefined
            };
        });
    };

    const mapType = (typeTestQuestionId: TypeTestQuestion): QuestionForm["type"] => {
        switch (typeTestQuestionId) {
            case 1: return "singleChoice";
            case 2: return "multipleChoice";
            case 3: return "writeCode";
            case 4: return "fixCode";
            case 5: return "openAnswer";
            default: return "singleChoice";
        }
    };


    const handleNext = async () => {
        try {
            await formTest.validateFields([
                'numberOfTests',
                'name',
                'selectedSkill',
                'numberOfAttempts',
                'maxTime',
                'level',
            ]);
            if (currentStep === 0) {

                if (!isEditProcess) {
                    await fetchQuestions();
                }

                setCurrentStep(currentStep + 1);
            }
        } catch (error) {
            console.log('Validation Failed:', error);
        }
    };

    const fetchQuestions = async () => {
        setLoading(true);

        if (!testSettings) {
            console.error("No test settings found for the selected level.");
            return;
        }

        let requestAI = "";
        let difficultyLevel = "beginner";
        let types = testSettings["junior"];

        if (formData.level === "junior") {
            difficultyLevel = "beginner";
            types = testSettings["junior"];
        } else if (formData.level === "middle") {
            difficultyLevel = "intermediate";
            types = testSettings["middle"];
        } else if (formData.level === "senior") {
            difficultyLevel = "advanced";
            types = testSettings["senior"];
        }

        if (formData.isOneLanguage) {

            let language = "Ukrainian";

            if (formData.isOneLanguage) {
                language = formData.selectedLanguage === "en" ? "English" : formData.selectedLanguage === "ua" ? "Ukrainian" : "Russian";
            }
            else {
                language = i18n.language === "en" ? "English" : i18n.language === "uk" ? "Ukrainian" : "Russian";
            }

            requestAI = ` 
                Generate ${formData.numberOfTests} unique programming questions on the topic of ${formData.selectedSkill} at a ${difficultyLevel} level.

                Make sure the output strictly adheres to the following guidelines:

                1. Provide the output as a JSON array, with no additional text, comments, or explanations. The output should begin with "[" and end with "]".
                2. Avoid any control characters, such as newlines or tabs, within string values. All strings must be properly escaped and conform to JSON standards.
                3. Distribute the question types as follows:
                - ${types.singleChoicePercentage}% for single-choice questions.
                - ${types.multipleChoicePercentage}% for multiple-choice questions.
                - ${types.writeCodePercentage}% for questions requiring writing code.
                - ${types.fixCodePercentage}% for questions about fixing code.
                - ${types.openAnswerPercentage}% for open-ended questions.
            
                For single-choice and multiple-choice questions:
                - The "options" field should be an array containing the available options.
                - For single-choice questions:
                    - Include exactly four options in the "options" array.
                    - The "correctAnswer" field should match exactly one of the options.
                - For multiple-choice questions:
                    - Include at least two options in the "options" array.
                    - Ensure the "correctAnswer" field matches one or more of the options.
                    - Avoid situations where three options are correct, and the fourth means "all of the above".
                    - If such a situation arises, add extra answer choices to maintain clarity.

                For questions requiring code writing or code fixing:
                - For "writeCode" questions:
                    - Include a prompt that specifies what needs to be written.
                    - Ensure the "correctAnswer" field contains the complete solution code.
                    - The "correctAnswer" field must have text answer.
                    
                - For "fixCode" questions:
                    - The "question" field should be without code and only text question.
                    - Include a "code" field with the code snippet containing one or more errors.
                    - Ensure the "correctAnswer" field contains the corrected version of the code.
            
                - For open-ended questions:
                    - Provide a prompt that encourages a detailed written response.
                    - The "correctAnswer" field must have text answer.
            
                Format the output as a JSON object with fields:
                - id: Unique question ID.
                - "question": The question text.
                - "options": An array of options (if applicable).
                - "language": "en", "ru", or "ua".
                - "numberOfQuestion": The original question number.
                - "correctAnswer": A string or array that matches the correct answer(s), or empty for open-ended questions.
                - "type": The type of question ("singleChoice", "multipleChoice", "writeCode", "fixCode", "openAnswer").
                - "code": For "writeCode" or "fixCode", include the relevant code snippet.
            
                Ensure all fields are fully validated for proper JSON escaping and formatting. Avoid any control characters like newlines or tabs within the strings.

                Ensure that the questions generated are different from the ones generated in the previous request.
            
                The response must be in ${language} and should reflect diverse and unique question formulations for this topic.

                Return only the JSON array, formatted correctly, without additional commentary or explanation.
            `;
        }
        else {
            requestAI = ` 
                Generate ${formData.numberOfTests} completely unique programming questions on the topic of ${formData.selectedSkill} at a ${difficultyLevel} level.

                Each unique question must be translated into three languages:  
                - English ("en")  
                - Russian ("ru")  
                - Ukrainian ("ua")  

                The final JSON array must contain **exactly ${formData.numberOfTests * 3}** total entries, ensuring that each question is unique and appears in three language variations.

                **Strict JSON Formatting Requirements:**
                1. The response must be a **valid JSON array** with no additional text, comments, or control characters (e.g., newlines, tabs, or trailing commas).
                2. All strings must be properly escaped (e.g., double quotes " inside strings must be escaped as \\").
                3. Ensure that the JSON is **minified** (no unnecessary spaces or line breaks).
                4. The JSON must strictly follow the structure and fields described below.

                **Question types distribution:**
                - ${types.singleChoicePercentage}% for single-choice questions.
                - ${types.multipleChoicePercentage}% for multiple-choice questions.
                - ${types.writeCodePercentage}% for questions requiring writing code.
                - ${types.fixCodePercentage}% for questions about fixing code.
                - ${types.openAnswerPercentage}% for open-ended questions.

                **Formatting for each question:**
                - **"id"**: Unique question ID.
                - **"question"**: The question text.
                - **"language"**: "en", "ru", or "ua".
                - **"numberOfQuestion"**: The original question number.
                - **"options"**: Answer choices (if applicable).
                - **"correctAnswer"**: A string or array that matches the correct answer(s), or empty for open-ended questions.
                - **"numberPoints"**: The question's score (default **5**).
                - **"type"**: Question type ("singleChoice", "multipleChoice", "writeCode", "fixCode", "openAnswer").

                **Example of correctly formatted output:**
                [
                    {
                        "id": 1,
                        "question": "What is a variable?",
                        "language": "en",
                        "numberOfQuestion": 1,
                        "options": ["Data type", "Storage location", "Function", "Loop"],
                        "correctAnswer": "Storage location",
                        "numberPoints": 5,
                        "type": "singleChoice"
                    },
                    {
                        "id": 2,
                        "question": "Что такое переменная?",
                        "language": "ru",
                        "numberOfQuestion": 1,
                        "options": ["Тип данных", "Место хранения", "Функция", "Цикл"],
                        "correctAnswer": "Место хранения",
                        "numberPoints": 5,
                        "type": "singleChoice"
                    },
                    {
                        "id": 3,
                        "question": "Що таке змінна?",
                        "language": "ua",
                        "numberOfQuestion": 1,
                        "options": ["Тип даних", "Місце зберігання", "Функція", "Цикл"],
                        "correctAnswer": "Місце зберігання",
                        "numberPoints": 5,
                        "type": "singleChoice"
                    }
                ]

                **Strict Validation Rules:**
                - Ensure there are **exactly ${formData.numberOfTests} unique questions**, each translated into three languages.
                - The total number of entries must be **exactly ${formData.numberOfTests * 3}**.
                - All fields must be present and follow the correct data types.
                - Do not include any additional fields or properties outside the specified structure.
                - Ensure proper JSON escaping (e.g., double quotes inside strings must be escaped as \\).
                - The response must **only** contain the JSON array, without any additional text, headers, or comments.
            
                **Important:**
                - If the response does not meet these requirements, it will be rejected.
                - Double-check the JSON for syntax errors, trailing commas, or invalid characters before returning it.
            
                Return **only** the JSON array, without any explanations, headers, or comments.
            `;
        }

        let success = false;
        const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

        const getUniqueQuestions = (
            existingQuestions: QuestionForm[],
            newQuestions: QuestionForm[]
        ): QuestionForm[] => {
            const existingSet = new Set(existingQuestions.map(q => `${q.question}-${q.type}`));

            return newQuestions.filter(q => !existingSet.has(`${q.question}-${q.type}`));
        };

        let allQuestions: QuestionForm[] = [];

        while (!success) {
            try {
                const requiredQuestionsCount = formData.isOneLanguage
                    ? formData.numberOfTests
                    : formData.numberOfTests * 3;

                while (allQuestions.length < requiredQuestionsCount) {
                    console.log(`Fetching batch (count questions now ${allQuestions.length}/${requiredQuestionsCount})...`);
                    const newQuestions = await fetchQuestionsToAI(requestAI);
                    const uniqueQuestions = getUniqueQuestions(allQuestions, newQuestions);

                    const filteredQuestions = uniqueQuestions.filter((question) => {
                        if (question.type === "singleChoice" || question.type === "multipleChoice") {
                            return Array.isArray(question.options) && question.options.length > 0;
                        }
                        return true;
                    });

                    allQuestions = [...allQuestions, ...filteredQuestions];

                    if (!formData.isOneLanguage) {
                        const groupByNumber = allQuestions.reduce((acc, q) => {
                            acc[q.numberOfQuestion] = acc[q.numberOfQuestion] || [];
                            acc[q.numberOfQuestion].push(q);
                            return acc;
                        }, {} as Record<number, QuestionForm[]>);

                        const completeGroups = Object.values(groupByNumber).filter(group => {
                            const langSet = new Set(group.map(q => q.language));
                            return langSet.size === 3;
                        });

                        allQuestions = completeGroups.flat();

                        console.log("Valid full triplets count:", completeGroups.length);
                    }

                    if (!formData.isOneLanguage && allQuestions.length >= requiredQuestionsCount) {
                        break;
                    }
                }

                allQuestions.forEach((question) => {
                    const matchingSetting = testPointSetting.find(
                        (setting) => setting.questionTypeName == question.type
                    );

                    if (matchingSetting) {
                        question.numberPoints = matchingSetting.defaultPoints;
                    }

                    if (question.type != 'singleChoice' && question.type != 'multipleChoice') {
                        question.options = [];
                    }
                });

                console.log("Final unique questions:", allQuestions);
                setQuestions(allQuestions);
                success = true;
            } catch (error) {
                console.error("Error fetching questions:", error);
                // allQuestions = [];
                await sleep(3000);
            }
        }

        setLoading(false);
    };

    const fetchQuestionsToAI = async (requestAI: string): Promise<QuestionForm[]> => {
        const genAI = new GoogleGenerativeAI(API_AI_KEY);
        const model = genAI.getGenerativeModel({
            model: GEMINI_MODEL,
            generationConfig: {
                responseMimeType: "application/json"
            }
        });

        const result = await model.generateContent(requestAI);

        const rawResponse = result.response.text().trim();
        const jsonStart = rawResponse.indexOf("[");
        const jsonEnd = rawResponse.lastIndexOf("]") + 1;

        if (jsonStart === -1 || jsonEnd === 0) {
            throw new Error("Response does not contain a valid JSON array.");
        }

        const cleanedResponse = rawResponse.substring(jsonStart, jsonEnd);

        return JSON.parse(cleanedResponse) as QuestionForm[];
    };

    const handleFormChange = (changedValues: any) => {
        setFormData((prev: any) => ({
            ...prev,
            ...changedValues,
        }));
    };

    const transformDataToTest = (formData: TestForm, questionsToProvideCount: number): Test => {
        const skillFounded = applicationStore.skills.find(s => s.title === formData.selectedSkill);

        if (existTest != null && isEditProcess == true) {
            return {
                id: existTest.id,
                name: formData.name,
                level: formData.level,
                questionsToProvideCount: questionsToProvideCount,
                language: existTest.language,
                description: formData.description,
                numberOfQuestions: formData.numberOfTests ?? existTest.testQuestions.length,
                skillId: Number(skillFounded?.skillId) ?? existTest.skillId,
                companyId: existTest.companyId ?? null,
                isPublic: false,
                isActive: existTest.isActive,
                isDeleted: false,
                numberOfAttempts: formData.numberOfAttempts,
                maxTimeMinutes: formData.maxTime,
                testQuestions: questions.map((q) => ({
                    id: q.id,
                    code: q.code ?? null,
                    language: q.language,
                    numberOfQuestion: q.numberOfQuestion,
                    numberPoints: q.numberPoints ?? 1,
                    prompt: q.prompt ?? null,
                    name: q.question,
                    testId: existTest.id,
                    typeTestQuestionId: q.type === "singleChoice"
                        ? TypeTestQuestion.SingleChoice
                        : q.type === "multipleChoice"
                            ? TypeTestQuestion.MultipleChoice
                            : q.type === "writeCode"
                                ? TypeTestQuestion.WriteCode
                                : q.type === "fixCode"
                                    ? TypeTestQuestion.FixCode
                                    : q.type === "openAnswer"
                                        ? TypeTestQuestion.OpenAnswer
                                        : TypeTestQuestion.SingleChoice,
                    correctAnswer: Array.isArray(q.correctAnswer) ? q.correctAnswer.join(", ") : q.correctAnswer ?? null,
                    testOptions: q.options?.map((optionName, index) => ({
                        id: existTest.testQuestions.find(tq => tq.id === q.id)?.testOptions[index]?.id ?? 0,
                        name: optionName,
                        isCorrect: Array.isArray(q.correctAnswer)
                            ? q.correctAnswer.includes(optionName)
                            : q.correctAnswer === optionName,
                        testQuestionId: q.id,
                    })) ?? [],
                })),
            };
        }

        return {
            id: 0,
            name: formData.name,
            level: formData.level,
            questionsToProvideCount: questionsToProvideCount,
            language: formData.isOneLanguage ? formData.selectedLanguage : "all",
            description: formData.description,
            numberOfQuestions: formData.numberOfTests,
            skillId: Number(skillFounded?.skillId) ?? null,
            companyId: Number(applicationStore.company.Id) ?? 0,
            isPublic: false,
            isActive: true,
            isDeleted: false,
            numberOfAttempts: formData.numberOfAttempts,
            maxTimeMinutes: formData.maxTime,
            testQuestions: questions.map((q) => ({
                id: 0,
                code: q.code ?? null,
                language: q.language,
                numberOfQuestion: q.numberOfQuestion,
                correctAnswer: Array.isArray(q.correctAnswer) ? q.correctAnswer.join(", ") : q.correctAnswer ?? null,
                numberPoints: q.numberPoints ?? 1,
                prompt: q.prompt ?? null,
                typeTestQuestionId: q.type === "singleChoice"
                    ? TypeTestQuestion.SingleChoice
                    : q.type === "multipleChoice"
                        ? TypeTestQuestion.MultipleChoice
                        : q.type === "writeCode"
                            ? TypeTestQuestion.WriteCode
                            : q.type === "fixCode"
                                ? TypeTestQuestion.FixCode
                                : q.type === "openAnswer"
                                    ? TypeTestQuestion.OpenAnswer
                                    : TypeTestQuestion.SingleChoice,
                name: q.question,
                testId: 0,
                testOptions: q.options == null ? [] : q.options.map((option, optionIndex) => ({
                    id: 0,
                    name: option,
                    isCorrect: Array.isArray(q.correctAnswer)
                        ? q.correctAnswer.includes(option)
                        : option == q.correctAnswer,
                    testQuestionId: 0,
                })),
            })),
        };
    };


    const handleFinish = async () => {
        const questionsToProvideCount = formTest.getFieldValue('questionsToProvideCount');

        if (!formData.isOneLanguage) {
            if (questionsToProvideCount > questions.filter(q => q.language == "en").length) {
                message.error(t("add_test.questionsToProvideCountError", { count: formData.questionsToProvideCount }));
                return;
            }
        }
        else {
            if (questionsToProvideCount > questions.length) {
                message.error(t("add_test.questionsToProvideCountError", { count: formData.questionsToProvideCount }));
                return;
            }
        }

        if (questions.length >= levelSettings.numberQuestions) {
            const model = transformDataToTest(formData, questionsToProvideCount);

            setLoading(true);

            if (existTest != null && isEditProcess) {
                await testsStore.updateTest(existTest.id, model);
            }
            else {
                await testsStore.createNew(model, isOverride);
            }

            setLoading(false);

            message.success("Success");
            navigateToTests();
        }
        else {
            message.error(
                t("add_test.minimum_questions_error", { minimum: 5 })
            );
        }
    };

    const navigateToTests = () => {
        navigate(`/${applicationStore.company.Tenant}/tests`);
    }

    const handleGetMinTimeForTest = () => {
        const level = formData.level;
        const questionsToProvideCount = formTest.getFieldValue('questionsToProvideCount');

        if (!level || !questionsToProvideCount || !testSettings) {
            return levelSettings.maxTimeMin;
        }

        let types = testSettings["junior"];

        if (formData.level === "junior") {
            types = testSettings["junior"];
        } else if (formData.level === "middle") {
            ;
            types = testSettings["middle"];
        } else if (formData.level === "senior") {
            types = testSettings["senior"];
        }

        const totalTimeInSeconds =
            (types.singleChoicePercentage / 100) * questionsToProvideCount * timePerQuestionTypeInSeconds.singleChoice +
            (types.multipleChoicePercentage / 100) * questionsToProvideCount * timePerQuestionTypeInSeconds.multipleChoice +
            (types.writeCodePercentage / 100) * questionsToProvideCount * timePerQuestionTypeInSeconds.writeCode +
            (types.fixCodePercentage / 100) * questionsToProvideCount * timePerQuestionTypeInSeconds.fixCode +
            (types.openAnswerPercentage / 100) * questionsToProvideCount * timePerQuestionTypeInSeconds.openAnswer;

        const totalTimeInMinutes = Math.ceil(totalTimeInSeconds / 60) + 1;

        return totalTimeInMinutes;
    };

    const handleLevelChange = (value: string) => {

        if (!testSettings) {
            return;
        }

        let level: TestLevelSetting = testSettings["junior"];

        switch (value) {
            case "junior":
                level = testSettings["junior"];
                break;
            case "middle":
                level = testSettings["middle"];
                break;
            case "senior":
                level = testSettings["senior"];
                break;
            default:
                break;
        }

        const minMinutesValue = handleGetMinTimeForTest();

        setLevelSettings({ maxTimeMin: minMinutesValue, attemptsMin: level.minNumberAttempts, numberQuestions: level.minNumberOfQuestons });
        formTest.setFieldsValue({
            maxTime: minMinutesValue,
            numberOfAttempts: level.minNumberAttempts,
            numberOfTests: level.minNumberOfQuestons
        });

        setFormData((prevValue) => ({
            ...prevValue,
            maxTime: minMinutesValue,
            numberOfAttempts: level.minNumberAttempts,
            numberOfTests: level.minNumberOfQuestons
        }));

        updateNameField(formData.selectedSkill, value);
        checkTestExistence(formData.selectedSkill, value);
    };

    const updateNameField = (skill: string, level: string) => {
        if (skill && level) {
            formTest.setFieldsValue({
                name: `${capitalize(level)} level test for ${skill}`,
            });

            setFormData((prevValue) => ({
                ...prevValue,
                name: `${capitalize(level)} level test for ${skill}`,
            }));
        }
    };

    const capitalize = (str: string) => {
        if (!str) return "";
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const handleSkillChange = (value: string) => {
        setFormData((prevValue) => ({
            ...prevValue,
            maxTime: handleGetMinTimeForTest(),
            selectedSkill: value,
        }));

        formTest.setFieldsValue({ maxTime: handleGetMinTimeForTest() });
        updateNameField(value, formData.level);
        checkTestExistence(value, formData.level);
    };

    const checkTestExistence = (skillTitle: string, level: string) => {
        const existSkill = toJS(applicationStore.skills.find(s => s.title == skillTitle));

        const exists = allCompanyTests.some(test => test.skillId == existSkill?.skillId && test.level === level);
        setIsOverride(exists);
    };

    return (
        <Layout
            headerStyle={1}
            footerStyle={2}
            breadcrumbTitle={isEditProcess ? t("add_test.editTest") : t("add_test.addTest")}
        >
            <Spin spinning={loading} style={{ minHeight: 600 }}>
                <div className="text-center" style={{ margin: '20px 20px' }}>
                    <Steps current={currentStep}>
                        <Step title={t("add_test.Step1")} />
                        <Step title={t("add_test.Step2")} />
                    </Steps>

                    <div style={{ padding: "20px" }}>
                        {currentStep === 0 && (
                            <Form
                                form={formTest}
                                layout="vertical"
                                onFinish={handleFinish}
                                onValuesChange={handleFormChange}
                                initialValues={formData}
                                style={{ maxWidth: "600px", margin: "0 auto" }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        gap: "20px",
                                    }}
                                >
                                    <Form.Item
                                        label={t("add_test.SelectSkill")}
                                        name="selectedSkill"
                                        rules={[{ required: true, message: t("add_test.Please select a skill!") }]}
                                        style={{ flex: "1 1 48%" }}
                                    >
                                        <Select
                                            style={{ color: "black", width: "100%" }}
                                            placeholder={t("add_test.SelectSkill")}
                                            options={applicationStore.skills.map((skill: Skill) => ({
                                                label: skill.title,
                                                value: skill.title,
                                            }))}
                                            onChange={handleSkillChange}
                                            dropdownStyle={{ color: "black" }}
                                            disabled={isEditProcess}
                                            showSearch
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            optionFilterProp="label"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label={t("add_test.Level")}
                                        name="level"
                                        rules={[{ required: true, message: t("add_test.Please select a level!") }]}
                                        style={{ flex: "1 1 48%" }}
                                    >
                                        <Select
                                            style={{ color: "black", width: "100%" }}
                                            options={[
                                                { label: "Junior", value: "junior" },
                                                { label: "Middle", value: "middle" },
                                                { label: "Senior", value: "senior" }
                                            ]}
                                            onChange={handleLevelChange}
                                            dropdownStyle={{ color: "black" }}
                                            disabled={isEditProcess}
                                        />
                                    </Form.Item>
                                </div>

                                <Form.Item
                                    label={t("add_test.Name")}
                                    name="name"
                                    rules={[
                                        {
                                            required: true,
                                            message: t("add_test.Please input the name!"),
                                        },
                                    ]}
                                >
                                    <Input style={{ width: "100%" }} />
                                </Form.Item>

                                {!isEditProcess && (
                                    <Form.Item
                                        label={t("add_test.NumberQuestionsForGeneration")}
                                        name="numberOfTests"
                                        rules={[
                                            {
                                                required: true,
                                                message: t("add_test.Please input the number of tests!"),
                                            },
                                            {
                                                type: "number",
                                                min: 1,
                                                message: t("add_test.NumberGreaterThan0"),
                                            },
                                            {
                                                type: "number",
                                                min: levelSettings.attemptsMin,
                                                message: t(`add_test.MinimumQuestionsIs ${levelSettings.attemptsMin}`),
                                            },
                                        ]}
                                    >
                                        <InputNumber style={{ width: "100%" }} min={levelSettings.numberQuestions} />
                                    </Form.Item>
                                )}

                                {isEditProcess && (
                                    <Form.Item
                                        label={t("add_test.NumberQuestions")}
                                    >
                                        <InputNumber value={existTest?.testQuestions.length} disabled={true} style={{ width: "100%" }} />
                                    </Form.Item>
                                )}

                                <Form.Item
                                    label={t("add_test.questionsToProvideCount")}
                                    name="questionsToProvideCount"
                                    rules={[
                                        {
                                            required: true,
                                            message: t("add_test.Please input the number of tests provided to user"),
                                        },
                                        {
                                            type: "number",
                                            min: 1,
                                            message: t("add_test.NumberGreaterThan0"),
                                        },
                                        {
                                            type: "number",
                                            min: levelSettings.attemptsMin,
                                            message: t("add_test.MinimumQuestionsIs", { min: levelSettings.attemptsMin }),
                                        },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (isEditProcess && existTest != null) {
                                                    if (value <= existTest?.testQuestions.length) {
                                                        return Promise.resolve();
                                                    }
                                                }
                                                else {
                                                    const numberOfTests = getFieldValue('numberOfTests');
                                                    if (value <= numberOfTests) {
                                                        return Promise.resolve();
                                                    }
                                                }

                                                return Promise.reject(new Error(t("add_test.QuestionsToProvideCannotExceedNumberOfTests")));
                                            },
                                        }),
                                    ]}
                                >
                                    <InputNumber
                                        style={{ width: "100%" }}
                                        min={levelSettings.numberQuestions}
                                        onChange={(value) => {
                                            const minTime = handleGetMinTimeForTest();
                                            setFormData({ ...formData, maxTime: minTime });
                                            formTest.setFieldsValue({ maxTime: minTime });
                                        }}
                                    />
                                </Form.Item>

                                {isOverride && (
                                    <div style={{ display: "flex" }}>
                                        <Form.Item name="overrideTest" valuePropName="checked">
                                            <Checkbox defaultChecked disabled={true}>
                                                {t("add_test.OverrideTest")}
                                            </Checkbox>
                                        </Form.Item>
                                    </div>
                                )}

                                {!isEditProcess && applicationStore?.siteConfig?.isShowLanguage && (
                                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <Form.Item name="isOneLanguage" valuePropName="checked" style={{ textAlign: "left" }}>
                                            <Checkbox name="isOneLanguage">
                                                {t("add_test.GenerateForOneLanguage")}
                                            </Checkbox>
                                        </Form.Item>

                                        {formData.isOneLanguage && (
                                            <Form.Item
                                                name="selectedLanguage"
                                                rules={[{ required: true, message: t("add_test.SelectLanguage") }]}
                                            >
                                                <Select placeholder={t("add_test.ChooseLanguage")} style={{ width: "200px" }}>
                                                    <Select.Option value="ua">{t("add_test.Ukrainian")}</Select.Option>
                                                    <Select.Option value="ru">{t("add_test.Russian")}</Select.Option>
                                                    <Select.Option value="en">{t("add_test.English")}</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        )}
                                    </div>
                                )}

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        gap: "20px",
                                    }}
                                >
                                    <Form.Item
                                        label={t("add_test.NumberAttempts")}
                                        name="numberOfAttempts"
                                        rules={[
                                            {
                                                required: true,
                                                message: t("add_test.Please input the number of attempts!"),
                                            },
                                            {
                                                type: "number",
                                                min: 1,
                                                message: t("add_test.NumberGreaterThan0"),
                                            },
                                            {
                                                type: "number",
                                                min: levelSettings.attemptsMin,
                                                message: t(`add_test.MinimumAttemptsIs ${levelSettings.attemptsMin}`),
                                            },
                                        ]}
                                        style={{ flex: "1 1 48%" }}
                                    >
                                        <InputNumber style={{ width: "100%" }} min={levelSettings.attemptsMin} />
                                    </Form.Item>

                                    <Form.Item
                                        label={`${t("add_test.MaxTime")} (${t("add_test.minutes")})`}
                                        name="maxTime"
                                        rules={[
                                            {
                                                required: true,
                                                message: t("add_test.Please input the maximum time!"),
                                            },
                                            {
                                                type: "number",
                                                min: 1,
                                                message: t("add_test.NumberGreaterThan0"),
                                            },
                                            {
                                                type: "number",
                                                min: levelSettings.maxTimeMin,
                                                message: t(`add_test.MinimumTimeIs ${levelSettings.maxTimeMin} ${t("add_test.minutes")}`),
                                            },
                                        ]}
                                        style={{ flex: "1 1 48%" }}
                                    >
                                        <InputNumber style={{ width: "100%" }} min={handleGetMinTimeForTest()} />
                                    </Form.Item>
                                </div>

                                <Form.Item
                                    label={t("add_test.Description")}
                                    name="description"
                                >
                                    <Input.TextArea
                                        rows={4}
                                        style={{ width: "100%" }}
                                    />
                                </Form.Item>

                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <Button
                                        type="primary"
                                        onClick={navigateToTests}
                                        style={{ backgroundColor: "red", borderColor: "red", color: "#fff" }}
                                    >
                                        {t("add_test.Previous")}
                                    </Button>
                                    <Button
                                        type="primary"
                                        onClick={async () => await handleNext()}
                                    >
                                        {t("add_test.Next")}
                                    </Button>
                                </div>
                            </Form>
                        )}

                        {currentStep === 1 && (
                            <AddTestFormQustions
                                isOneLanguageQuestions={formData.isOneLanguage}
                                currentStep={currentStep}
                                isEditProcess={isEditProcess}
                                setCurrentStep={setCurrentStep}
                                handleFinish={handleFinish}
                                questions={questions}
                                setQuestions={setQuestions}
                            />
                        )}
                    </div>
                </div>
            </Spin>
        </Layout>
    );
});

export default EditOrAddTest;